import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { NotFoundException } from "@zxing/library";

interface Props {
  /** Called whenever a code is decoded. Implementation should debounce duplicates. */
  onDecode: (text: string) => void;
  className?: string;
}

/**
 * Camera-based QR scanner using @zxing/browser. Picks the back camera by
 * default. The host of an event uses this to scan attendee tickets.
 */
const QrScanner = ({ onDecode, className }: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();

    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        if (cancelled) return;
        const back =
          devices.find((d) => /back|rear|environment/i.test(d.label)) ??
          devices[devices.length - 1] ??
          devices[0];
        if (!videoRef.current) return;

        const controls = await reader.decodeFromVideoDevice(
          back?.deviceId,
          videoRef.current,
          (result, err) => {
            if (result) onDecode(result.getText());
            if (err && !(err instanceof NotFoundException)) {
              // Other errors are noisy frame failures; ignore.
            }
          },
        );
        controlsRef.current = controls;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not start camera");
        setActive(false);
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [active, onDecode]);

  return (
    <div className={className ?? "flex flex-col gap-3"}>
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-black">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          muted
          playsInline
        />
        {!active ? (
          <button
            type="button"
            onClick={() => setActive(true)}
            className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm font-medium text-white"
          >
            Tap to start camera
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Point the camera at a guest's ticket QR.
        </p>
      )}
      {active ? (
        <button
          type="button"
          onClick={() => setActive(false)}
          className="self-start text-xs text-muted-foreground hover:text-foreground"
        >
          Stop camera
        </button>
      ) : null}
    </div>
  );
};

export default QrScanner;
